import React, { Component, createRef } from 'react';

// CSS
import '../Courses/Courses.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch, uploadCourseMedia } from '../../Helper/Api';

// Components
import Header from '../../components/Header/Header';
import { Panel, Confirm } from '../../components/UI/Overlays';
import { EmptyState, TableWait, Tip } from '../../components/UI/Feedback';
import { Toolbar } from '../../components/UI/Controls';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';

// Properties
const properties = require('../../properties.json');

const EMPTY_FORM = { id: null, title: '', tag: '', videoUrl: '', photos: [] };

/** Manages the portfolio "Project" cards shown on the public Gallery page -- see
 *  GalleryProjectController on the backend. Each project is a title + short tag + several photos
 *  + one optional behind-the-scenes video, matching the reference design the teacher sent. */
class GalleryProjects extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
    }

    state = {
        projects: [],
        loading: true,
        form: EMPTY_FORM,
        saving: false,
        uploadingPhotos: {}, // fileName -> percent, while a batch of photos is uploading
        uploadingVideo: false,
        videoProgress: 0,

        globalFilter: null,

        // The form used to sit permanently above the list, and editing
        // scrolled the page back to the top to reach it. It opens over the
        // list now; `form.id` still decides whether it is adding or editing.
        formOpen: false,

        // the project the teacher asked to delete, held while the confirm is up
        doomed: null,
        deleting: false,
    }

    componentDidMount() {
        document.title = properties.site_name + " | معرض الأعمال";
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
            return;
        }
        this.load();
    }

    load = () => {
        this.setState({ loading: true });
        apiFetch("/gallery-projects/mine")
            .then(projects => this.setState({ projects, loading: false }))
            .catch(error => {
                console.error('Error:', error);
                this.setState({ loading: false });
            });
    }

    resetForm = () => this.setState({ form: EMPTY_FORM });

    openAdd = () => this.setState({ form: EMPTY_FORM, formOpen: true });

    closeForm = () => this.setState({ formOpen: false, form: EMPTY_FORM });

    editProject = (project) => {
        this.setState({
            form: { id: project.id, title: project.title || '', tag: project.tag || '', videoUrl: project.videoUrl || '', photos: project.photos || [] },
            formOpen: true,
        });
    }

    // The browser's own confirm box is gone; the question is asked in the
    // product's own confirm card instead. The delete behind it is unchanged.
    deleteProject = (project) => {
        this.setState({ deleting: true });
        apiFetch(`/gallery-projects/${project.id}`, { method: "DELETE" })
            .then(() => {
                this.setState({ doomed: null, deleting: false });
                this.load();
            })
            .catch(error => {
                console.error('Error:', error);
                this.setState({ doomed: null, deleting: false });
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    uploadPhotos = async (event) => {
        const files = event.files || [];
        if (!files.length) return;
        const newUrls = [];
        for (const file of files) {
            this.setState(s => ({ uploadingPhotos: { ...s.uploadingPhotos, [file.name]: 0 } }));
            try {
                const url = await uploadCourseMedia(file, (pct) =>
                    this.setState(s => ({ uploadingPhotos: { ...s.uploadingPhotos, [file.name]: pct } }))
                );
                newUrls.push(url);
            } catch (error) {
                console.error('Error:', error);
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: `فشل رفع ${file.name}` });
            } finally {
                this.setState(s => {
                    const remaining = { ...s.uploadingPhotos };
                    delete remaining[file.name];
                    return { uploadingPhotos: remaining };
                });
            }
        }
        this.setState(s => ({ form: { ...s.form, photos: [...s.form.photos, ...newUrls] } }));
        event.options && event.options.clear && event.options.clear();
    }

    removePhoto = (index) => {
        this.setState(s => ({ form: { ...s.form, photos: s.form.photos.filter((_, i) => i !== index) } }));
    }

    // Cover has no dedicated field -- GalleryProjectPhoto's sortOrder doubles as it (see backend
    // GalleryProjectService), so index 0 in this array IS the cover. Moving the chosen photo there
    // is the whole implementation, and it keeps working identically on edit (photos load back into
    // this same array from the saved order).
    setCover = (index) => {
        this.setState(s => {
            const photos = [...s.form.photos];
            const [chosen] = photos.splice(index, 1);
            photos.unshift(chosen);
            return { form: { ...s.form, photos } };
        });
    }

    uploadVideo = async (event) => {
        const file = event.files && event.files[0];
        if (!file) return;
        this.setState({ uploadingVideo: true, videoProgress: 0 });
        try {
            const url = await uploadCourseMedia(file, (pct) => this.setState({ videoProgress: pct }));
            this.setState(s => ({ form: { ...s.form, videoUrl: url }, uploadingVideo: false }));
        } catch (error) {
            console.error('Error:', error);
            this.setState({ uploadingVideo: false });
            this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'فشل رفع الفيديو' });
        } finally {
            event.options && event.options.clear && event.options.clear();
        }
    }

    save = () => {
        const { form, projects } = this.state;
        if (!form.title || form.photos.length === 0) {
            this.toast.current.show({ severity: 'error', summary: 'تنبيه', detail: 'من فضلك أدخل عنوان المشروع وارفع صورة واحدة على الأقل' });
            return;
        }
        this.setState({ saving: true });
        const body = {
            title: form.title,
            tag: form.tag,
            videoUrl: form.videoUrl || null,
            photos: form.photos,
            sortOrder: form.id ? undefined : projects.length,
        };
        const request = form.id
            ? apiFetch(`/gallery-projects/${form.id}`, { method: "PUT", body })
            : apiFetch("/gallery-projects", { method: "POST", body });
        request
            .then(() => {
                // the save finished the job, so the panel closes and the list
                // behind it goes and fetches what was just written
                this.setState({ saving: false, form: EMPTY_FORM, formOpen: false });
                this.toast.current.show({ severity: 'success', summary: 'تم بنجاح', detail: 'تم حفظ المشروع' });
                this.load();
            })
            .catch(error => {
                console.error('Error:', error);
                this.setState({ saving: false });
                this.toast.current.show({ severity: 'error', summary: 'لم تتم العملية', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    render() {
        const { projects, loading, form, saving, uploadingPhotos, uploadingVideo, videoProgress, doomed, deleting } = this.state;

        // The save stays out of reach until the form can actually be saved --
        // the same two things save() checks, read here so the button can say
        // so before it is pressed rather than after.
        const canSave = !!(form.title && form.title.trim()) && form.photos.length > 0;

        // the project as the teacher recognises it: its cover, its title, and
        // the tag that goes under it on the public page
        const titleBody = (row) => {
            const cover = row.photos && row.photos[0];
            return (
                <span className="ui-row-lead">
                    {cover ? (
                        <img className="thumb" src={cover} alt="" loading="lazy" />
                    ) : (
                        <span className="thumb is-empty" aria-hidden="true">
                            <i className="pi pi-images"></i>
                        </span>
                    )}
                    <span className="tx">
                        <b>{row.title}</b>
                        <small>{row.tag || 'من غير تصنيف'}</small>
                    </span>
                </span>
            );
        };

        // what the project actually carries, counted from the row itself
        const contentBody = (row) => {
            const photos = (row.photos || []).length;
            if (!photos && !row.videoUrl) {
                return <span className="ui-bits is-none">لسه فاضي</span>;
            }
            return (
                <span className="ui-bits">
                    {photos ? (
                        <span className="bit" title="صور">
                            <i className="pi pi-image"></i>
                            {photos}
                        </span>
                    ) : null}
                    {row.videoUrl ? (
                        <span className="bit" title="فيديو">
                            <i className="pi pi-video"></i>
                            فيديو
                        </span>
                    ) : null}
                </span>
            );
        };

        const actionBodyTemplate = (row) => (
            <React.Fragment>
                <Tip text="تعديل">
                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-secondary p-ml-2" onClick={() => this.editProject(row)} />
                </Tip>
                <Tip text="حذف">
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" onClick={() => this.setState({ doomed: row })} />
                </Tip>
            </React.Fragment>
        );

        const saveButton = (
            <Button
                className={`add-button ${saving ? "loading" : ""}`}
                label={saving ? "جاري الحفظ..." : (form.id ? "حفظ التعديلات" : "إضافة المشروع")}
                disabled={!canSave || saving}
                onClick={this.save}
            />
        );

        return (
            <>
                <Header
                    pageName="معرض الأعمال"
                    action={{ label: "إضافة مشروع", icon: "pi pi-plus", onClick: this.openAdd }}
                />
                <Toast ref={this.toast}></Toast>

                {/* Adding and editing are the same form; `form.id` is the only
                    thing that differs, exactly as it always did. */}
                <Panel
                    visible={this.state.formOpen}
                    onHide={this.closeForm}
                    title={form.id ? "تعديل مشروع" : "إضافة مشروع"}
                    subtitle="المشروع ده هيظهر في معرض الأعمال على موقعك"
                    actions={saveButton}
                >
                    <div className="add-lesson-page in-panel">
                        <div className="content">
                            <div className="container">
                                <section className="ui-sec" style={{ "--d": "0ms" }}>
                                    <h3>
                                        <span className="n">١</span>
                                        <span className="t">
                                            بيانات المشروع
                                            <small>العنوان والسطر الصغير اللي بيظهر تحته</small>
                                        </span>
                                    </h3>
                                    <div className="p-grid p-fluid">
                                        <div className="p-col-12 p-md-4">
                                            <div className="p-inputgroup">
                                                <span className="p-float-label">
                                                    <InputText
                                                        id="project-title"
                                                        value={form.title}
                                                        onChange={(e) => this.setState({ form: { ...form, title: e.target.value } })}
                                                    />
                                                    <label htmlFor="project-title">عنوان المشروع</label>
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-col-12 p-md-4">
                                            <div className="p-inputgroup">
                                                <span className="p-float-label">
                                                    <InputText
                                                        id="project-tag"
                                                        value={form.tag}
                                                        onChange={(e) => this.setState({ form: { ...form, tag: e.target.value } })}
                                                        placeholder="مثال: تصميم حضري · من الاسكتش للرندر"
                                                    />
                                                    <label htmlFor="project-tag">
                                                        تصنيف/وصف مختصر
                                                        <small className="ui-fieldhint">اختياري</small>
                                                    </label>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section className="ui-sec" style={{ "--d": "70ms" }}>
                                    <h3>
                                        <span className="n">٢</span>
                                        <span className="t">
                                            صور المشروع
                                            <small>أول صورة هي صورة الغلاف — تقدر تغيّرها من أي صورة تحت</small>
                                        </span>
                                    </h3>
                                    <div className="p-grid p-fluid">
                                        <div className="p-col-12 p-md-12">
                                            <FileUpload
                                                name="galleryPhotos"
                                                customUpload
                                                uploadHandler={this.uploadPhotos}
                                                auto
                                                multiple
                                                accept="image/*"
                                                maxFileSize={20000000}
                                                chooseLabel="رفع صور المشروع"
                                                uploadLabel="رفع"
                                                cancelLabel="إلغاء"
                                                mode="basic"
                                            />
                                            {Object.entries(uploadingPhotos).map(([name, pct]) => (
                                                <div className="upload-progress-row" key={name}>
                                                    <span>{name}</span>
                                                    <ProgressBar value={pct} style={{ flex: 1, height: '10px' }} />
                                                    <span>{pct}%</span>
                                                </div>
                                            ))}
                                            {form.photos.length > 0 ? (
                                                <div className="gallery-photo-list">
                                                    {form.photos.map((url, i) => (
                                                        <div className={`gallery-photo-row ${i === 0 ? 'is-cover' : ''}`} key={url + i}>
                                                            <img src={url} alt={`صورة ${i + 1}`} />
                                                            {i === 0 ? (
                                                                <span className="gallery-photo-cover-badge"><i className="pi pi-star-fill" /> صورة الغلاف</span>
                                                            ) : (
                                                                <Button label="تعيين كغلاف" className="p-button-text p-button-sm gallery-photo-set-cover" onClick={() => this.setCover(i)} />
                                                            )}
                                                            <Button icon="pi pi-times" className="p-button-rounded p-button-danger p-button-text" onClick={() => this.removePhoto(i)} />
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="ui-waiting">ارفع صورة واحدة على الأقل — من غير صور مش هينفع تحفظ المشروع</div>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                <section className="ui-sec" style={{ "--d": "140ms" }}>
                                    <h3>
                                        <span className="n">٣</span>
                                        <span className="t">
                                            فيديو المشروع
                                            <small>اختياري — فيديو من وراء الكواليس يظهر مع المشروع</small>
                                        </span>
                                    </h3>
                                    <div className="p-grid p-fluid">
                                        <div className="p-col-12 p-md-12">
                                            <FileUpload
                                                name="galleryVideo"
                                                customUpload
                                                uploadHandler={this.uploadVideo}
                                                auto
                                                disabled={uploadingVideo}
                                                multiple={false}
                                                accept="video/*"
                                                maxFileSize={2000000000}
                                                chooseLabel={form.videoUrl ? "استبدال فيديو المشروع" : "رفع فيديو المشروع (اختياري)"}
                                                uploadLabel="رفع"
                                                cancelLabel="إلغاء"
                                                mode="basic"
                                            />
                                            {uploadingVideo && (
                                                <div className="upload-progress-row">
                                                    <ProgressBar value={videoProgress} style={{ flex: 1, height: '10px' }} />
                                                    <span>{videoProgress}%</span>
                                                </div>
                                            )}
                                            {!uploadingVideo && form.videoUrl && (
                                                <div className="uploaded-file-row">
                                                    <i className="pi pi-check-circle" style={{ color: 'green' }} />
                                                    <span>تم رفع الفيديو</span>
                                                    <Button icon="pi pi-times" className="p-button-rounded p-button-text p-button-danger" onClick={() => this.setState({ form: { ...form, videoUrl: '' } })} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                </Panel>

                <Confirm
                    visible={!!doomed}
                    onHide={() => this.setState({ doomed: null })}
                    name={doomed ? doomed.title : ""}
                    note="المشروع هيتشال بكل صوره من معرض الأعمال على موقعك، ومش هينفع ترجع عنه."
                    busy={deleting}
                    onConfirm={() => this.deleteProject(doomed)}
                />

                <div className="content">
                    <div className="container">
                        <div className="card">
                            <Card>
                                <Toolbar
                                    value={this.state.globalFilter || ''}
                                    onSearch={(v) => this.setState({ globalFilter: v })}
                                    placeholder="بحث في المشاريع"
                                    count={loading ? null : `${projects.length} مشروع`}
                                />

                                <DataTable
                                    sortable
                                    globalFilter={this.state.globalFilter}
                                    className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                                    value={projects}
                                    paginator
                                    rows={10}
                                    rowsPerPageOptions={[10, 20, 50]}
                                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                                    emptyMessage={
                                        loading ? (
                                            <TableWait />
                                        ) : (
                                            <EmptyState
                                                title="لسه مفيش مشاريع"
                                                hint="ضيف أول مشروع يظهر في معرض أعمالك على الموقع"
                                                icon="pi pi-images"
                                                action={{ label: "إضافة مشروع", onClick: this.openAdd }}
                                            />
                                        )
                                    }
                                >
                                    <Column sortable field="title" body={titleBody} header="المشروع"></Column>
                                    <Column body={contentBody} header="المحتوى"></Column>
                                    <Column header="تعديل / حذف" body={actionBodyTemplate}></Column>
                                </DataTable>
                            </Card>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default GalleryProjects;
